import * as THREE from "three";
import type { AlignmentData, RoomPlanData, RoomPlanObject } from "../types.ts";

const WALL_COLOR = 0x4a9eff;
const DOOR_COLOR = 0xffb347;
const WINDOW_COLOR = 0x7ed5e5;
const OPENING_COLOR = 0xcccccc;
const DEFAULT_OBJ_COLOR = 0x9a9a9a;

const CATEGORY_COLORS: Record<string, number> = {
  bed: 0x9b7ed5, chair: 0xe5a85a, table: 0x7ea5d5, sofa: 0x8fd57e,
  storage: 0xb8b8b8, oven: 0xd57e7e, fridge: 0x7ed5d5, washer: 0xd5d57e,
  dishwasher: 0xc6c6c6, stairs: 0x7a7a7a, toilet: 0xd5c5d5, sink: 0xa0c0d5,
  television: 0x3a3a3a, stove: 0xc07070, bathtub: 0xc5d5e5,
};

/**
 * Parametric RoomPlan visualisation — renders walls, openings and furniture
 * from a `_rooms.json` blob as simple Three.js boxes.
 *
 * Geometry is built in RoomPlan's native coords (ARKit Y-up, metres). Two
 * placement modes:
 *   – Auto-centre (used when no alignment is known): offset so plan bbox
 *     is centred on origin and floor sits at y=0.
 *   – Aligned: apply yaw + XZ translation from an `AlignmentData` so walls
 *     overlay the RTABmap mesh.
 */
export class RoomPlanLayer {
  readonly group: THREE.Group;
  private autoCenterOffset: THREE.Vector3;
  private floorYOffset: number;
  private disposed = false;

  constructor(data: RoomPlanData, glbScene: THREE.Group | null = null) {
    this.group = new THREE.Group();
    this.group.name = "roomplan_layer";

    const [minX, minY, minZ] = data.bounds.min;
    const [maxX, , maxZ] = data.bounds.max;
    const cx = (minX + maxX) / 2;
    const cz = (minZ + maxZ) / 2;
    this.autoCenterOffset = new THREE.Vector3(-cx, -minY, -cz);
    this.floorYOffset = -minY;

    if (glbScene) {
      // Production path: use the baked `_model.glb` from the converter.
      // Walls already have door/window cut-outs; categories/IDs are in node
      // extras for selection/filtering downstream.
      recolorRoomPlanGlb(glbScene);
      this.group.add(glbScene);
    } else {
      // Fallback: parametric boxes from `_rooms.json` only.
      for (const w of data.walls) this._addBox(w.transform, w.dimensions, WALL_COLOR, 0.5);
      for (const d of data.doors) this._addBox(d.transform, d.dimensions, DOOR_COLOR, 0.8);
      for (const wi of data.windows) this._addBox(wi.transform, wi.dimensions, WINDOW_COLOR, 0.55);
      for (const op of data.openings) this._addBox(op.transform, op.dimensions, OPENING_COLOR, 0.3);
      for (const obj of data.objects) this._addObject(obj);
    }

    this.setAlignment(null);
    this.group.visible = false;
  }

  /** Place the layer by an alignment transform, or auto-centre when null. */
  setAlignment(align: AlignmentData | null): void {
    if (align) {
      this.group.rotation.set(0, align.yaw, 0);
      this.group.position.set(align.tx, this.floorYOffset, align.tz);
    } else {
      this.group.rotation.set(0, 0, 0);
      this.group.position.copy(this.autoCenterOffset);
    }
  }

  get visible(): boolean {
    return this.group.visible;
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        const m = obj.material as THREE.Material | THREE.Material[];
        if (Array.isArray(m)) m.forEach((x) => x.dispose());
        else m.dispose();
      }
    });
    this.group.removeFromParent();
  }

  private _addBox(
    transform: number[],
    dims: { width: number; height: number; thickness: number },
    color: number,
    opacity: number,
  ): void {
    if (dims.width <= 0 || dims.height <= 0 || dims.thickness <= 0) return;
    const geo = new THREE.BoxGeometry(dims.width, dims.height, dims.thickness);
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: opacity < 1,
      opacity,
      depthWrite: opacity >= 0.95,
    });
    const mesh = new THREE.Mesh(geo, mat);
    _applyWorldTransform(mesh, transform);
    this.group.add(mesh);
  }

  private _addObject(obj: RoomPlanObject): void {
    const color = CATEGORY_COLORS[obj.category] ?? DEFAULT_OBJ_COLOR;
    this._addBox(obj.transform, obj.dimensions, color, 0.65);
  }
}


function _categoryStyle(category: string): { color: number; opacity: number } {
  switch (category) {
    case "wall":    return { color: WALL_COLOR,    opacity: 0.5  };
    case "door":    return { color: DOOR_COLOR,    opacity: 0.8  };
    case "window":  return { color: WINDOW_COLOR,  opacity: 0.55 };
    case "opening": return { color: OPENING_COLOR, opacity: 0.3  };
    default:
      return { color: CATEGORY_COLORS[category] ?? DEFAULT_OBJ_COLOR, opacity: 0.65 };
  }
}

function _categoryFromMesh(obj: THREE.Mesh): string {
  const cat = obj.userData?.category;
  if (typeof cat === "string" && cat) return cat;
  // fallback: node name is "{category}_{12-hex-id}"
  const m = obj.name.match(/^([a-z]+)_[0-9a-f]+$/i);
  return m ? m[1].toLowerCase() : "";
}

export function recolorRoomPlanGlb(scene: THREE.Group): void {
  scene.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    const { color, opacity } = _categoryStyle(_categoryFromMesh(obj));
    const old = obj.material as THREE.Material | THREE.Material[];
    if (Array.isArray(old)) old.forEach((m) => m.dispose());
    else old.dispose();
    obj.material = new THREE.MeshBasicMaterial({
      color,
      transparent: opacity < 1,
      opacity,
      depthWrite: opacity >= 0.95,
      side: THREE.DoubleSide,
    });
  });
}

/**
 * Apply a row-major 4×4 matrix (16 floats) stored in rooms.json to a Three.js
 * object. Position + orientation are extracted; the matrix's scale component
 * is discarded because geometry is built with the already-world-scaled
 * dimensions.
 */
function _applyWorldTransform(obj: THREE.Object3D, rowMajor: number[]): void {
  if (rowMajor.length !== 16) return;
  const m = new THREE.Matrix4();
  m.set(
    rowMajor[0],  rowMajor[1],  rowMajor[2],  rowMajor[3],
    rowMajor[4],  rowMajor[5],  rowMajor[6],  rowMajor[7],
    rowMajor[8],  rowMajor[9],  rowMajor[10], rowMajor[11],
    rowMajor[12], rowMajor[13], rowMajor[14], rowMajor[15],
  );
  const pos = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  m.decompose(pos, quat, scale);
  obj.position.copy(pos);
  obj.quaternion.copy(quat);
  // scale intentionally ignored — geometry carries final dimensions.
}
